// PWA (MOB-001, SHELL-021): registers /sw.js in production and offers "New version available"
// with a Reload action when a new worker is waiting or the API asks for a newer web build.
import { useEffect } from 'react'
import { toast } from 'sonner'
import { t } from './i18n'
import { usePlatformStatus } from './platform'

/** Build number baked in at build time (VITE_BUILD), 0 in development. */
export const WEB_BUILD: number = Number(import.meta.env.VITE_BUILD ?? 0) || 0

const TOAST_ID = 'mgs-new-version'
const RELOADED_KEY = 'mgs-reloaded-for-build'
let registration: ServiceWorkerRegistration | null = null
let reloading = false

/** Resolves with the active registration once /sw.js is registered (web push uses it). */
export function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (registration) return Promise.resolve(registration)
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return Promise.resolve(null)
  return navigator.serviceWorker.getRegistration('/').then((r) => r ?? null)
}

function reloadOnce(): void {
  if (reloading) return
  reloading = true
  window.location.reload()
}

/** Activates the waiting worker (if any) and reloads the page. */
export function applyUpdate(): void {
  const waiting = registration?.waiting
  if (waiting) {
    navigator.serviceWorker.addEventListener('controllerchange', reloadOnce, { once: true })
    waiting.postMessage({ type: 'SKIP_WAITING' })
    setTimeout(reloadOnce, 3000)
  } else {
    reloadOnce()
  }
}

/** Shows the sticky update toast once. `beforeReload` runs when the person presses Reload. */
export function promptUpdate(beforeReload?: () => void): void {
  toast(t('New version available'), {
    id: TOAST_ID,
    description: t('Reload to get the latest MG AI School.'),
    duration: Infinity,
    action: {
      label: t('Reload'),
      onClick: () => {
        beforeReload?.()
        applyUpdate()
      },
    },
  })
}

function watchWaiting(reg: ServiceWorkerRegistration): void {
  if (reg.waiting && navigator.serviceWorker.controller) promptUpdate()
  reg.addEventListener('updatefound', () => {
    const worker = reg.installing
    if (!worker) return
    worker.addEventListener('statechange', () => {
      // A new worker installed while an old one controls the page means a new deploy.
      if (worker.state === 'installed' && navigator.serviceWorker.controller) promptUpdate()
    })
  })
}

/** Registers the service worker in production builds only. */
export async function registerServiceWorker(): Promise<void> {
  if (!import.meta.env.PROD) return
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  try {
    registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
    watchWaiting(registration)
    // Look for a new deploy every 30 minutes while the tab is open.
    setInterval(() => void registration?.update().catch(() => undefined), 30 * 60_000)
  } catch {
    /* PWA is progressive: the app works without it */
  }
}

/** True when the API requires a newer web build than this bundle (force update gate). */
export function needsUpdate(minWebBuild: number, build = WEB_BUILD): boolean {
  return Number(minWebBuild) > build
}

/**
 * platform-account-12: a minimum above the deployed build leaves the tab older after the reload, and the prompt came
 * back on every page for good. The tab remembers the minimum it reloaded for (this session) and asks once.
 */
export function rememberReloadFor(minWebBuild: number): void {
  try {
    sessionStorage.setItem(RELOADED_KEY, String(minWebBuild))
  } catch {
    /* storage off: the prompt may come back, as before */
  }
}

/** True when the API asks for a newer build and this tab has not already reloaded for that minimum. */
export function shouldPromptUpdate(minWebBuild: number, build = WEB_BUILD): boolean {
  let reloadedFor = 0
  try {
    reloadedFor = Number(sessionStorage.getItem(RELOADED_KEY)) || 0
  } catch {
    /* storage off */
  }
  return needsUpdate(minWebBuild, build) && reloadedFor !== Number(minWebBuild)
}

/** Mounted once in the app shell: prompts when /api/platform/status asks for a newer build. */
export function useUpdatePrompt(): void {
  const { minWebBuild } = usePlatformStatus()
  useEffect(() => {
    if (shouldPromptUpdate(minWebBuild)) promptUpdate(() => rememberReloadFor(minWebBuild))
  }, [minWebBuild])
}
